// The find box on History's People, Works, Events and Other traditions
// pages, enhanced.
// Without this file each is a plain GET form with a Show button, and the
// controller hides the same rows.
//
// With it, every keystroke filters the rows already in the document — every
// row is always rendered, the excluded ones `hidden` — so there is no request,
// no frame and nothing for Back to repaint. The address is REPLACED, never
// pushed: a link copied mid-search reproduces it, and typing a name does not
// leave a history entry per letter.

// The same fold as Findable.fold, which wrote each row's data-search. The
// query is the only thing folded here.
export function fold(text) {
  return text
    .normalize("NFKD")
    .replace(/\p{M}/gu, "")
    .toLowerCase()
    .replace(/\p{Lm}|[^\p{L}\p{N}]+/gu, " ")
    .replace(/ +/g, " ")
    .trim();
}

// HistoryHelper#index_status, in the same words. `nouns` is the page's
// { one, many }: "entry"/"entries", "work"/"works".
export function status(shown, total, periods, nouns) {
  const count = (n, one, many) => `${n} ${n === 1 ? one : many}`;
  const all = count(total, nouns.one, nouns.many);
  if (shown === 0) return `None of ${all}`;
  const across = count(periods, "period", "periods");
  if (shown === total) return `${all} across ${across}`;
  return `${shown} of ${all}, in ${across}`;
}

export function apply(root) {
  const form = root.querySelector("[data-index-filter]");
  const query = form.querySelector("[data-index-query]");
  const flag = form.querySelector("[data-index-flag]");
  const terms = fold(query.value).split(" ").filter(Boolean);
  const flagOnly = Boolean(flag?.checked);

  let shown = 0;
  let periods = 0;
  root.querySelectorAll("[data-index-period]").forEach((section) => {
    let here = 0;
    section.querySelectorAll("[data-search]").forEach((row) => {
      const match = (!flagOnly || row.dataset.flag === "true")
        && terms.every((term) => row.dataset.search.includes(term));
      row.hidden = !match;
      if (match) here += 1;
    });

    section.hidden = here === 0;
    const count = section.querySelector("[data-index-count]");
    if (count) count.textContent = String(here);

    // The era strip follows, or it offers periods the list has emptied.
    const chip = root.querySelector(`[data-era-link="${section.id}"]`);
    if (chip) {
      chip.closest("li").hidden = here === 0;
      const chipCount = chip.querySelector("[data-era-count]");
      if (chipCount) chipCount.textContent = String(here);
    }

    shown += here;
    if (here) periods += 1;
  });

  // Other traditions files its periods under a heading per tradition. A
  // tradition the filter empties goes, and so does its label on the strip.
  const line = root.querySelector("[data-index-status]");
  const nouns = { one: line.dataset.nounOne, many: line.dataset.nounMany };
  root.querySelectorAll("[data-index-group]").forEach((group) => {
    const kept = group.querySelectorAll("[data-search]:not([hidden])").length;
    group.hidden = kept === 0;
    const count = group.querySelector("[data-index-group-count]");
    if (count) count.textContent = `${kept} ${kept === 1 ? nouns.one : nouns.many}`;
  });
  root.querySelectorAll("[data-era-group-label]").forEach((label) => {
    const chips = root.querySelectorAll(`.era-strip__item[data-era-group="${label.dataset.eraGroup}"]`);
    label.hidden = [...chips].every((chip) => chip.hidden);
  });

  root.querySelector("[data-era-strip]")?.dispatchEvent(new CustomEvent("era-strip:changed"));

  line.textContent = status(shown, Number(line.dataset.total), periods, nouns);
  root.querySelector("[data-index-empty]").hidden = shown > 0;

  const url = new URL(window.location.href);
  if (query.value.trim()) url.searchParams.set("q", query.value.trim());
  else url.searchParams.delete("q");
  if (flag) {
    if (flagOnly) url.searchParams.set(flag.name, "1");
    else url.searchParams.delete(flag.name);
  }
  // Turbo keeps its restoration id in history.state, so it is carried over.
  window.history.replaceState(window.history.state, "", url);
}

export function enhance(root) {
  if (root.dataset.historyIndexReady) return;
  root.dataset.historyIndexReady = "true";

  const form = root.querySelector("[data-index-filter]");
  if (!form) return;
  form.querySelector("[data-index-apply]").hidden = true;

  form.addEventListener("input", () => apply(root));
  form.addEventListener("change", () => apply(root));
  // Enter in the box has nothing left to do: the list already says the answer.
  form.addEventListener("submit", (event) => event.preventDefault());

  // The reset link clears the form rather than loading the page again, so
  // focus goes back to the box the reader was typing in.
  root.querySelector("[data-index-reset]")?.addEventListener("click", (event) => {
    event.preventDefault();
    form.querySelector("[data-index-query]").value = "";
    const flag = form.querySelector("[data-index-flag]");
    if (flag) flag.checked = false;
    apply(root);
    form.querySelector("[data-index-query]").focus();
  });
}

export function start() {
  document.querySelectorAll("[data-history-index]").forEach(enhance);
}

document.addEventListener("DOMContentLoaded", start);
document.addEventListener("turbo:load", start);
